import { Notify as QNotify } from 'quasar'

// Do a runtime-safe lookup of Quasar's Notify so this module can be imported
// in non-browser environments (tests, Node). If Quasar/Notify is unavailable
// or running in Node, we provide a no-op shim.
function getNotifyImpl() {
  if (typeof window === 'undefined') {
    return {
      create: () => {},
      setDefaults: () => {},
      registerType: () => {}
    }
  }
  return QNotify || {
    create: () => {},
    setDefaults: () => {},
    registerType: () => {}
  }
}

// Shared no-op handler to avoid allocating a new function for every
// notification's default close action.
const NOOP = () => {}

const DEFAULTS = {
  position: 'bottom', // bottom centers horizontally in Quasar
  timeout: 3000, // Auto-hide after 3 seconds
  multiLine: false,
    caption: '',
  // Use a round white close icon action by default (Quasar will auto-dismiss
  // the notification after action handler runs unless action.noDismiss === true)
  actions: [
    { icon: 'close', color: 'blue', size:'sm',round: true, handler: NOOP }
  ],


}

export function useNotify() {
  const Notify = getNotifyImpl()

  function notify(options) {
    if (typeof options === 'string') {
      Notify.create({ ...DEFAULTS, message: options })
      return
    }
    Notify.create({ ...DEFAULTS, ...options })
  }

  // Predefined type helpers (alias to match Quasar's `type` style)
  function positive(message, opts = {}) {
    Notify.create({ ...DEFAULTS, type: 'positive', color: 'positive', icon: 'check_circle', message, ...opts })
  }
  function negative(message, opts = {}) {
    Notify.create({ ...DEFAULTS, type: 'negative', color: 'negative', icon: 'error', message, ...opts })
  }
  function warning(message, opts = {}) {
    Notify.create({ ...DEFAULTS, type: 'warning', color: 'warning', icon: 'warning', message, ...opts })
  }
  function info(message, opts = {}) {
    Notify.create({ ...DEFAULTS, type: 'info', color: 'info', icon: 'info', message, ...opts })
  }

  // Ongoing returns the notify updater function so caller can later update/dismiss it
  function ongoing(message, opts = {}) {
    // Use group: false so the returned handler can be used to update this
    // notification (Quasar only allows updates on non-grouped notifications).
    const n = Notify.create({ ...DEFAULTS, type: 'ongoing', timeout: 0, group: false, message, ...opts })
    return n
  }

  // Create an updatable notification (alias for ongoing with clearer name)
  function updatable(message, opts = {}) {
    return ongoing(message, { ...opts, group: false, timeout: 0 })
  }

  // Convenience helper to allow HTML messages (caller must sanitize)
  function html(message, opts = {}) {
    if (typeof message === 'string') {
      return Notify.create({ ...DEFAULTS, html: true, message, ...opts })
    }
    return Notify.create({ ...DEFAULTS, html: true, ...message })
  }

  // Expose wrappers for Quasar's global defaults and custom type registration
  function setDefaults(opts = {}) {
    const N = getNotifyImpl()
    if (N.setDefaults) N.setDefaults(opts)
  }

  function registerType(name, cfg = {}) {
    const N = getNotifyImpl()
    if (N.registerType) N.registerType(name, cfg)
  }

  // Helper to dismiss a notification returned by Notify.create()
  // If passed a function (the returned handler), call it to dismiss.
  function dismiss(handle) {
    if (typeof handle === 'function') {
      // Calling with no args dismisses the notification; calling with an
      // object updates it instead.
      handle()
    }
  }

  return { notify, positive, negative, warning, info, ongoing, updatable, html, setDefaults, registerType, dismiss }
}
